import 'promise-all-settled-polyfill'
import { Suspense, useEffect } from 'react'
import { RecoilRoot } from 'recoil'
import RecoilNexus from 'recoil-nexus'
import { SWRConfig } from 'swr'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import useHotjar from 'react-use-hotjar'
import { ThemeProvider } from 'styled-components'
import { LibraryWrapper, theme } from 'yolda-web-ui'
import { Amplify } from '@aws-amplify/core'

import { useScript } from './components/hooks'
import Common from './components/templates/common'
import Main from './components/templates/main'
import Public from './components/templates/public'
import GlobalStyle from './Global.style'
import { IRouteConfig, routes } from './routers/routers'
import config from './config/env'
import { LanguageHelper, MixPanel, SentryHelper } from './utils/helpers'
import ErrorBoundary from './components/pages/ErrorBoundary'
import { LogoAnimation } from './components/atoms'
import modals from './utils/constants/Modals'
import NotFound from './components/pages/NotFound/NotFound'
import 'dayjs/locale/en'
import 'dayjs/locale/de'
import 'dayjs/locale/tr'

LanguageHelper.init()
MixPanel.init()
SentryHelper.init()
Amplify.configure(config.aws)

function App() {
  useScript('https://challenges.cloudflare.com/turnstile/v0/api.js')
  const { initHotjar } = useHotjar()

  useEffect(() => {
    config?.HotjarId && initHotjar(config.HotjarId, 6)
  }, [initHotjar])

  const renderRoutes = () => {
    return routes
      .filter((r: IRouteConfig) => r.enabled)
      .map((route: IRouteConfig) => {
        if (route.component) {
          const Wrapper = route.protectedRoute ? Main : Public
          const RouteComponent = route.component

          return (
            <Route
              key={route.key}
              path={route.url}
              element={
                <Common>
                  <Wrapper>
                    <RouteComponent key={route.key} />
                  </Wrapper>
                </Common>
              }
            />
          )
        }

        throw {
          message: `No component or render is available for route: ${route.url}`,
          code: 0,
        }
      })
  }

  return (
    <Suspense fallback={<LogoAnimation />}>
      <SWRConfig
        value={{
          revalidateOnMount: true,
          revalidateIfStale: true,
          shouldRetryOnError: false,
          focusThrottleInterval: 0,
          refreshInterval: 0,
          revalidateOnFocus: false,
          revalidateOnReconnect: false,
          refreshWhenOffline: false,
          refreshWhenHidden: false,
        }}
      >
        <ThemeProvider theme={theme}>
          <ErrorBoundary>
            <RecoilRoot>
              <RecoilNexus />
              <LibraryWrapper elements={{ ...modals }}>
                <BrowserRouter>
                  <Routes>
                    {renderRoutes()}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </BrowserRouter>
              </LibraryWrapper>
            </RecoilRoot>
          </ErrorBoundary>
        </ThemeProvider>
        <GlobalStyle />
      </SWRConfig>
    </Suspense>
  )
}

export default App
